'use client'

import {
  Modal as HeroModal,
  ModalProps as HeroModalProps,
  Button,
  useOverlayState
} from '@heroui/react'
import { forwardRef, ReactNode } from 'react'
import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'

// Re-export v3 compound subcomponents for convenience
const ModalDialog = HeroModal.Dialog
const ModalHeader = HeroModal.Header
const ModalBody = HeroModal.Body
const ModalFooter = HeroModal.Footer

export { ModalDialog, ModalHeader, ModalBody, ModalFooter, useOverlayState }

export interface ModalProps extends Omit<HeroModalProps, 'children'> {
  title?: string
  children?: ReactNode
  footer?: ReactNode
  closeButton?: boolean
  closeIcon?: IconName
  className?: string
  onClose?: () => void
  showCloseButton?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  placement?: 'auto' | 'top' | 'center' | 'bottom'
  scrollBehavior?: 'inside' | 'outside'
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ title, children, footer, closeButton = true, closeIcon = 'close', className, showCloseButton = true, size, placement, scrollBehavior, ...props }, ref) => {
    // Map legacy v2 size names onto valid HeroUI v3 Modal sizes.
    const SIZE_MAP: Record<string, string> = { xl: 'lg', '2xl': 'lg', '3xl': 'lg', '4xl': 'cover', '5xl': 'cover' }
    const v3Size = size ? (SIZE_MAP[size] ?? size) : undefined
    // `backdrop` is the v2 name; on v3 the dim treatment is `variant` on
    // Modal.Backdrop. Forwarding it as-is silently drops it, so translate here
    // rather than letting every caller discover that the prop does nothing.
    const { isOpen, onClose, backdrop, ...rest } = props as {
      isOpen?: boolean
      onClose?: () => void
      backdrop?: 'opaque' | 'blur' | 'transparent'
    }
    return (
      <HeroModal.Backdrop
        isOpen={isOpen}
        onOpenChange={(open: boolean) => {
          if (!open) onClose?.()
        }}
        variant={backdrop}
        {...(rest as any)}
      >
        <HeroModal.Container size={v3Size as any} placement={placement} scroll={scrollBehavior as any} className={className}>
          <HeroModal.Dialog>
            <>
              {title && (
                <HeroModal.Header className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{title}</h3>
                  {showCloseButton && closeButton && (
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      onPress={() => onClose?.()}
                      aria-label="Close modal"
                    >
                      <Icon icon={closeIcon} className="w-4 h-4" />
                    </Button>
                  )}
                </HeroModal.Header>
              )}
              {children && <HeroModal.Body>{children}</HeroModal.Body>}
              {footer && <HeroModal.Footer>{footer}</HeroModal.Footer>}
            </>
          </HeroModal.Dialog>
        </HeroModal.Container>
      </HeroModal.Backdrop>
    )
  }
)

Modal.displayName = 'Modal'

// Simplified Modal wrapper for common use cases
export interface SimpleModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  placement?: 'auto' | 'top' | 'center' | 'bottom'
  backdrop?: 'opaque' | 'blur' | 'transparent'
  scrollBehavior?: 'inside' | 'outside'
  closeOnOverlayClick?: boolean
  className?: string
}

export const SimpleModal = forwardRef<HTMLDivElement, SimpleModalProps>(
  ({ isOpen, onClose, title, children, footer, ...props }, ref) => {
    return (
      <Modal
        ref={ref}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        footer={footer}
        {...props}
      >
        {children}
      </Modal>
    )
  }
)

SimpleModal.displayName = 'SimpleModal'

export default Modal
